import React, { FunctionComponent, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Card, FloatingLabel, Form } from "react-bootstrap"
import { CenterLayout } from "../../presentationals/layouts/center"
import { Trans, withTranslation, WithTranslation } from "react-i18next"
import * as Yup from "yup"
import styles from "./password-reset.module.scss"
import { API } from "../../api/api"
import { ErrorAlert } from "../../components/error-alert/error-alert"
import { SubmitButton } from "../../presentationals/submit-button"
import { Formik, FormikHelpers } from "formik"
import Icon from "../../components/icon/icon"
import check2circle from "url:bootstrap-icons/icons/check2-circle.svg"
// import { zxcvbn } from "@zxcvbn-ts/core"

interface PasswordResetRequest {
    newPassword: string
    newPasswordConfirm: string
}

const PasswordReset: FunctionComponent<WithTranslation> = ({
    t,
}: WithTranslation) => {
    const [searchParams] = useSearchParams()
    const userId = searchParams.get("userId")
    const token = searchParams.get("token")
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean | null>(null)

    const validationSchema = Yup.object().shape({
        /* This is way better than the regex, but for now we need to have consistent behavior to the apps.
        newPassword: Yup.string().test(
            "pw-validation",
            t("errors.password_requirements"),
            (pw, ctx) => {
                if (pw) {
                    const res = zxcvbn(pw)
                    if (res.score < 3) {
                        return ctx.createError({
                            message: res.feedback.warning,
                        })
                    }
                    return true
                }
                return false
            }
        ),*/
        newPassword: Yup.string().matches(
            /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#&()\-+:;,.?~$*]).{9,64}$/,
            t("errors.password_requirements")
        ),
        newPasswordConfirm: Yup.string().test(
            "pw-confirm-validation",
            t("errors.password_confirm"),
            (pwConfirm, ctx) => {
                if (pwConfirm) {
                    return ctx.parent.newPassword === pwConfirm
                }
                return true
            }
        ),
    })

    const initialValue: PasswordResetRequest = {
        newPassword: "",
        newPasswordConfirm: "",
    }

    const onSubmit = async (
        values: PasswordResetRequest,
        { setSubmitting }: FormikHelpers<PasswordResetRequest>
    ) => {
        if (userId && token) {
            setSubmitting(true)
            const setNewPassword = async () => {
                const res = await API.USER.setNewPassword(
                    userId,
                    token,
                    values.newPassword
                )
                if (res === null) {
                    setSubmitting(false)
                    setSuccess(true)
                    setErrorMessage(null)
                } else {
                    setSubmitting(false)
                    setSuccess(false)
                    setErrorMessage(
                        res.error
                            ? t("generic.error_text", {
                                  errorMessage: res.error,
                              })
                            : t("generic.unknown_error_text")
                    )
                }
            }
            setNewPassword().catch((err) => {
                setSubmitting(false)
                setSuccess(false)
                setErrorMessage(
                    err.message
                        ? t("generic.error_text", {
                              errorMessage: err.message,
                          })
                        : t("generic.unknown_error_text")
                )
            })
        }
    }

    return (
        <CenterLayout>
            <Card className={styles.card}>
                <Card.Body className={styles.cardBody}>
                    <img
                        className={styles.logo}
                        src={String(
                            new URL("../../assets/logo.svg", import.meta.url)
                        )}
                        alt="Anio Logo"
                    />
                    <div className={styles.heading}>
                        <h1>
                            <Trans>password_reset.title</Trans>
                        </h1>
                        <hr></hr>
                    </div>

                    {success ? (
                        <>
                            <h2>
                                <Trans>password_reset.success_title</Trans>
                            </h2>
                            <Icon size="48px" color="green">
                                {check2circle}
                            </Icon>
                        </>
                    ) : (
                        <>
                            {userId && token ? (
                                <>
                                    {errorMessage && (
                                        <div>
                                            <ErrorAlert error={errorMessage} />
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                href="https://anio.zendesk.com/hc/de"
                                            >
                                                <Trans>generic.support</Trans>
                                            </a>
                                        </div>
                                    )}
                                    <Formik
                                        initialValues={initialValue}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}
                                    >
                                        {({
                                            values,
                                            errors,
                                            handleChange,
                                            handleBlur,
                                            handleSubmit,
                                            isSubmitting,
                                        }) => (
                                            <Form
                                                className={styles.form}
                                                onSubmit={handleSubmit}
                                            >
                                                <FloatingLabel
                                                    label={t(
                                                        "password_reset.new_password"
                                                    )}
                                                    controlId="newPassword"
                                                >
                                                    <Form.Control
                                                        type="password"
                                                        placeholder={t(
                                                            "password_reset.new_password"
                                                        )}
                                                        required
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        name="newPassword"
                                                        value={
                                                            values.newPassword
                                                        }
                                                        isInvalid={
                                                            !!errors.newPassword
                                                        }
                                                        autoComplete="off"
                                                    />
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        className={
                                                            styles.newPasswordFeedbackText
                                                        }
                                                    >
                                                        {errors.newPassword}
                                                    </Form.Control.Feedback>
                                                </FloatingLabel>
                                                <FloatingLabel
                                                    label={t(
                                                        "password_reset.new_password"
                                                    )}
                                                    controlId="newPasswordConfirm"
                                                >
                                                    <Form.Control
                                                        type="password"
                                                        placeholder={t(
                                                            "password_reset.new_password_confirm"
                                                        )}
                                                        required
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        name="newPasswordConfirm"
                                                        isInvalid={
                                                            !!errors.newPasswordConfirm
                                                        }
                                                        autoComplete="off"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {
                                                            errors.newPasswordConfirm
                                                        }
                                                    </Form.Control.Feedback>
                                                </FloatingLabel>
                                                <SubmitButton
                                                    className={
                                                        styles.submitButton
                                                    }
                                                    isSubmitting={isSubmitting}
                                                    isInvalid={
                                                        !!Object.keys(errors)
                                                            .length
                                                    }
                                                >
                                                    {t("generic.submit")}
                                                </SubmitButton>
                                            </Form>
                                        )}
                                    </Formik>
                                </>
                            ) : (
                                <>
                                    <h2>
                                        <Trans>generic.error_title</Trans>
                                    </h2>
                                    <p>
                                        <Trans>
                                            generic.unknown_error_text
                                        </Trans>
                                    </p>
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://anio.zendesk.com/hc/de"
                                    >
                                        <Trans>generic.support</Trans>
                                    </a>
                                </>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </CenterLayout>
    )
}

export default withTranslation()(PasswordReset)
